/**
 * TAS Command — Sistema Digitale di Gestione Operazioni SAR
 *
 * Copyright (c) 2026 Matteo MESSINA, Alfonso CECERE
 * Tutti i diritti riservati.
 *
 * Sviluppato dagli autori a titolo personale, al di fuori
 * dell'orario di servizio e senza utilizzo di risorse o
 * strumentazione dell'amministrazione di appartenenza.
 *
 * Qualsiasi riproduzione, distribuzione o utilizzo non
 * autorizzato è vietato ai sensi della L. 633/1941.
 *
 * Autori:
 *   Matteo MESSINA  — VE, Comando VVF Benevento, Servizio TAS
 *   Alfonso CECERE  — VE, Comando VVF Benevento, Servizio TAS
 */

// Vista Report condivisa fra i pannelli (Admin / ROP-ROR-RON / CON-DIR_CAM-COM_PROV).
// Layout e design unificati: KPI a card con accento colorato, pannelli a barre
// orizzontali/verticali e donut SVG. Lo stile è incapsulato in un <style> con
// selettori prefissati `.tas-report` per non interferire col resto dell'app.

import React, { useState, useEffect } from "react";
import { RefreshCw, FileDown, X, Check } from "lucide-react";
import { toast } from "sonner";
import { generatePDF } from "./AppCore";

const STYLE = `
.tas-report{
  --tr-card:#0e1626; --tr-card-2:#0c1322; --tr-border:#1d2840; --tr-border-soft:#172033;
  --tr-track:#1a2336; --tr-head:#eef3fa; --tr-label:#8a97ab; --tr-muted:#5f6c81; --tr-value:#d6deea;
  --tr-orange:#f5821f; --tr-blue:#4a90e2; --tr-purple:#a855f7; --tr-green:#2eb872;
  --tr-r:14px;
  color:var(--tr-head);
  font-family:"Inter",-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;
}
.tas-report *{box-sizing:border-box}

.tas-report .topbar{display:flex;align-items:center;justify-content:space-between;gap:16px;margin-bottom:22px;flex-wrap:wrap}
.tas-report .title-group{display:flex;align-items:center;gap:14px}
.tas-report .tg-badge{width:42px;height:42px;border-radius:11px;flex:0 0 auto;background:linear-gradient(135deg,#1a2740,#101a2e);border:1px solid var(--tr-border);display:grid;place-items:center;font-size:20px}
.tas-report .title-group h1{font-size:19px;margin:0;letter-spacing:.2px;font-weight:650;color:#fff}
.tas-report .title-group p{margin:2px 0 0;font-size:12.5px;color:var(--tr-label)}
.tas-report .actions{display:flex;gap:9px;flex-wrap:wrap}
.tas-report .pill{font-size:12px;color:var(--tr-value);background:var(--tr-card);border:1px solid var(--tr-border);border-radius:9px;padding:8px 13px;display:flex;align-items:center;gap:7px;white-space:nowrap}
.tas-report button.pill{cursor:pointer}
.tas-report button.pill:hover{background:#13233c}
.tas-report .pill .dot{width:7px;height:7px;border-radius:50%;background:var(--tr-green)}
.tas-report .filtro{display:flex;align-items:center;gap:8px;flex-wrap:wrap;background:var(--tr-card);border:1px solid var(--tr-border);border-radius:11px;padding:10px 13px;margin-bottom:18px}
.tas-report .filtro .fl{font-size:12px;color:var(--tr-label)}
.tas-report .filtro .fl-sep{margin-left:6px}
.tas-report .filtro .fin{font-size:12px;color:var(--tr-value);background:var(--tr-bg);border:1px solid var(--tr-border);border-radius:8px;padding:6px 9px}
.tas-report .filtro .fin:disabled{opacity:.5}
.tas-report .filtro .fin-date{font-family:ui-monospace,monospace}
.tas-report .filtro .fnota{font-size:11px;color:var(--tr-label);margin-left:auto}
.tas-report button.pill-go{border-color:var(--tr-orange);color:var(--tr-orange)}

.tas-report .kpis{display:grid;grid-template-columns:repeat(4,1fr);gap:14px;margin-bottom:14px}
.tas-report .kpi{background:linear-gradient(180deg,var(--tr-card),var(--tr-card-2));border:1px solid var(--tr-border);border-radius:var(--tr-r);padding:16px 16px 14px;position:relative;overflow:hidden}
.tas-report .kpi::before{content:"";position:absolute;left:0;top:0;bottom:0;width:3px;background:var(--tr-accent,var(--tr-blue))}
.tas-report .kpi .k-label{font-size:11.5px;color:var(--tr-label);text-transform:uppercase;letter-spacing:.7px;font-weight:600}
.tas-report .kpi .k-val{font-size:30px;font-weight:700;margin-top:8px;line-height:1;font-variant-numeric:tabular-nums;color:#fff}
.tas-report .kpi .k-sub{font-size:12px;color:var(--tr-muted);margin-top:7px}

.tas-report .grid{display:grid;grid-template-columns:1fr 1fr;gap:14px}
.tas-report .panel{background:linear-gradient(180deg,var(--tr-card),var(--tr-card-2));border:1px solid var(--tr-border);border-radius:var(--tr-r);padding:18px 18px 16px;display:flex;flex-direction:column;min-width:0}
.tas-report .panel h2{font-size:14px;margin:0 0 16px;font-weight:640;display:flex;align-items:center;gap:9px;color:#fff}
.tas-report .panel h2 .ic{font-size:15px;opacity:.95}
.tas-report .panel h2 .count{margin-left:auto;font-size:11.5px;color:var(--tr-label);font-weight:600;background:#101a2c;border:1px solid var(--tr-border-soft);border-radius:20px;padding:3px 10px}

.tas-report .bars{display:flex;flex-direction:column;gap:13px;margin-top:2px}
.tas-report .brow{display:grid;grid-template-columns:130px 1fr 34px;align-items:center;gap:12px}
.tas-report .brow .name{font-size:12.5px;color:var(--tr-label);text-align:right;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.tas-report .brow .track{height:13px;background:var(--tr-track);border-radius:7px;overflow:hidden;box-shadow:inset 0 0 0 1px rgba(255,255,255,.03)}
.tas-report .brow .fill{display:block;height:100%;border-radius:7px;background:var(--tr-c,var(--tr-blue));box-shadow:0 0 10px -3px var(--tr-c,var(--tr-blue));transition:width .6s cubic-bezier(.2,.7,.2,1)}
.tas-report .brow .val{font-size:13px;font-weight:650;text-align:right;color:var(--tr-value);font-variant-numeric:tabular-nums}
.tas-report .brow.lead .name{color:#cdd7e6;font-weight:600}
.tas-report .brow.lead .val{color:#fff}
.tas-report .empty{color:var(--tr-muted);font-size:12px;text-align:center;padding:18px 0}

.tas-report .trend{display:flex;align-items:flex-end;gap:26px;height:190px;padding:6px 8px 0;position:relative}
.tas-report .tcol{display:flex;flex-direction:column;align-items:center;gap:9px;flex:1;max-width:120px;justify-content:flex-end;height:100%}
.tas-report .tcol .tbar{width:62px;border-radius:8px 8px 3px 3px;background:linear-gradient(180deg,#ff9a3d,var(--tr-orange));position:relative;transition:height .6s cubic-bezier(.2,.7,.2,1);box-shadow:0 0 0 1px rgba(245,130,31,.15)}
.tas-report .tcol .tval{position:absolute;top:-24px;left:0;right:0;text-align:center;font-size:15px;font-weight:700;color:#fff}
.tas-report .tcol .tlab{font-size:12px;color:var(--tr-label);font-variant-numeric:tabular-nums}
.tas-report .trend-foot{display:flex;justify-content:space-between;margin-top:14px;padding-top:13px;border-top:1px solid var(--tr-border-soft);flex-wrap:wrap;gap:6px}
.tas-report .trend-foot .tf{font-size:12px;color:var(--tr-label)}
.tas-report .trend-foot .tf b{color:var(--tr-value);font-weight:650}
.tas-report .delta-up{color:var(--tr-green);font-weight:650}
.tas-report .delta-down{color:#ef6f6f;font-weight:650}

.tas-report .demo{display:grid;grid-template-columns:185px 1fr;gap:18px;align-items:center}
.tas-report .donut-wrap{display:flex;flex-direction:column;align-items:center;gap:12px}
.tas-report .donut{position:relative;width:140px;height:140px}
.tas-report .donut .center{position:absolute;inset:0;display:grid;place-content:center;text-align:center}
.tas-report .donut .center .n{font-size:30px;font-weight:750;line-height:1;color:#fff}
.tas-report .donut .center .t{font-size:10.5px;color:var(--tr-label);text-transform:uppercase;letter-spacing:1px;margin-top:2px}
.tas-report .legend{display:flex;flex-direction:column;gap:7px;width:100%}
.tas-report .legend .li{display:flex;align-items:center;gap:8px;font-size:12px}
.tas-report .legend .sw{width:9px;height:9px;border-radius:3px;flex:0 0 auto}
.tas-report .legend .li .ln{color:var(--tr-label);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.tas-report .legend .li .lv{margin-left:auto;color:var(--tr-value);font-weight:650;font-variant-numeric:tabular-nums}
.tas-report .legend .li .lp{color:var(--tr-muted);font-size:11px;width:40px;text-align:right}
.tas-report .age{display:flex;flex-direction:column;gap:10px;min-width:0}
.tas-report .age .ahead{font-size:11px;color:var(--tr-muted);text-transform:uppercase;letter-spacing:.8px;margin-bottom:2px;font-weight:600}
.tas-report .age .brow{grid-template-columns:54px 1fr 26px;gap:10px}
.tas-report .age .brow .name{font-size:12px}

.tas-report .empty-card{background:linear-gradient(180deg,var(--tr-card),var(--tr-card-2));border:1px solid var(--tr-border);border-radius:var(--tr-r);padding:48px 18px;text-align:center}
.tas-report .empty-card p{margin:0;color:var(--tr-label);font-size:13px}
.tas-report .empty-card p.sub{color:var(--tr-muted);font-size:11.5px;margin-top:6px}

@media(max-width:860px){
  .tas-report .kpis{grid-template-columns:repeat(2,1fr)}
  .tas-report .grid{grid-template-columns:1fr}
  .tas-report .demo{grid-template-columns:1fr}
  .tas-report .donut-wrap{flex-direction:row;justify-content:center;gap:26px}
}
`;

const DONUT_PALETTE = ["var(--tr-orange)", "var(--tr-blue)", "var(--tr-green)", "var(--tr-purple)", "#ef4444", "#eab308", "#06b6d4", "#ec4899"];

// Barre orizzontali: il primo elemento (valore più alto) è evidenziato come "lead".
const Bars = ({ data = [], color = "var(--tr-blue)", maxItems = 15, ageStyle = false }) => {
  const items = (data || []).slice(0, maxItems);
  if (!items.length) return <div className="empty">Nessun dato</div>;
  const maxVal = Math.max(...items.map(d => d.count), 1);
  return (
    <div className={`bars${ageStyle ? " age-bars" : ""}`}>
      {items.map((d, i) => (
        <div key={i} className={`brow${i === 0 && !ageStyle ? " lead" : ""}`}>
          <span className="name" title={d.label}>{d.label}</span>
          <span className="track"><span className="fill" style={{ width: `${Math.max(3, (d.count / maxVal) * 100)}%`, "--tr-c": color }} /></span>
          <span className="val">{d.count}</span>
        </div>
      ))}
    </div>
  );
};

// Trend mensile a barre verticali + footer con mese di punta e variazione %.
const Trend = ({ data = [] }) => {
  if (!data.length) return <div className="empty">Nessun dato</div>;
  const maxVal = Math.max(...data.map(d => d.count), 1);
  const peak = data.reduce((a, b) => (b.count > a.count ? b : a), data[0]);
  let delta = null;
  if (data.length >= 2) {
    const last = data[data.length - 1].count;
    const prev = data[data.length - 2].count;
    if (prev > 0) delta = Math.round(((last - prev) / prev) * 100);
    else if (last > 0) delta = 100;
  }
  return (
    <>
      <div className="trend">
        {data.map((d, i) => (
          <div className="tcol" key={i}>
            <div className="tbar" style={{ height: `${Math.max(2, (d.count / maxVal) * 100)}%` }}>
              <span className="tval">{d.count > 0 ? d.count : ""}</span>
            </div>
            <div className="tlab">{d.mese}</div>
          </div>
        ))}
      </div>
      <div className="trend-foot">
        <span className="tf">Mese di punta · <b>{peak.mese} ({peak.count})</b></span>
        {delta != null && (
          <span className="tf">
            <span className={delta >= 0 ? "delta-up" : "delta-down"}>{delta >= 0 ? "▲" : "▼"} {delta >= 0 ? "+" : ""}{delta}%</span> sul mese precedente
          </span>
        )}
      </div>
    </>
  );
};

// Donut SVG a partire dalla tecnica stroke-dasharray (circonferenza ≈ 100).
const Donut = ({ data = [] }) => {
  const items = (data || []).filter(d => d.count > 0);
  const total = items.reduce((s, d) => s + d.count, 0);
  if (total === 0) return <div className="empty">Nessun dato</div>;
  let acc = 0;
  const slices = items.map((d, i) => {
    const pct = (d.count / total) * 100;
    const slice = { ...d, pct, color: DONUT_PALETTE[i % DONUT_PALETTE.length], dash: `${pct} ${100 - pct}`, offset: 25 - acc };
    acc += pct;
    return slice;
  });
  return (
    <div className="donut-wrap">
      <div className="donut">
        <svg width="140" height="140" viewBox="0 0 42 42">
          <circle cx="21" cy="21" r="15.915" fill="none" stroke="var(--tr-track)" strokeWidth="6" />
          {slices.map((s, i) => (
            <circle key={i} cx="21" cy="21" r="15.915" fill="none" stroke={s.color} strokeWidth="6"
              strokeDasharray={s.dash} strokeDashoffset={s.offset} />
          ))}
        </svg>
        <div className="center"><div className="n">{total}</div><div className="t">totale</div></div>
      </div>
      <div className="legend">
        {slices.map((s, i) => (
          <div className="li" key={i}>
            <span className="sw" style={{ background: s.color }} />
            <span className="ln" title={s.label}>{s.label}</span>
            <span className="lv">{s.count}</span>
            <span className="lp">{Math.round(s.pct)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Footer (divider + riepilogo) per i pannelli a barre: mostra l'elemento con
// più casi e la sua quota percentuale sul totale, in stile come il trend.
const TopFoot = ({ data = [], leadText, totalText }) => {
  const items = (data || []).filter(d => d.count > 0);
  if (!items.length) return null;
  const total = items.reduce((s, d) => s + d.count, 0);
  const top = items.reduce((a, b) => (b.count > a.count ? b : a), items[0]);
  const pct = total > 0 ? Math.round((top.count / total) * 100) : 0;
  return (
    <div className="trend-foot">
      <span className="tf">{leadText} · <b>{top.label}</b></span>
      <span className="tf"><span className="delta-up">{pct}%</span> {totalText}</span>
    </div>
  );
};

const Panel = ({ icon, title, count, children }) => (
  <div className="panel">
    <h2><span className="ic">{icon}</span> {title}{count != null && <span className="count">{count}</span>}</h2>
    {children}
  </div>
);

const Kpi = ({ label, value, sub, accent = "var(--tr-blue)" }) => (
  <div className="kpi" style={{ "--tr-accent": accent }}>
    <div className="k-label">{label}</div>
    <div className="k-val">{value ?? "–"}</div>
    {sub !== undefined && <div className="k-sub">{sub}</div>}
  </div>
);

/**
 * Vista Report unificata.
 * @param data       payload /report (totali, trend_mensile, per_regione, dispersi…)
 * @param title      titolo (es. "Report Generale", "Report Regionale")
 * @param subtitle   sottotitolo descrittivo
 * @param onRefresh  callback pulsante Aggiorna
 * @param loading    spinner sul pulsante Aggiorna
 * @param isNational se true mostra il pannello "Operazioni per Regione"
 */
const MESI = [
  "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
  "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre",
];

const due = (n) => String(n).padStart(2, "0");
// Ultimo giorno del mese: `new Date(anno, mese, 0)` torna al giorno prima del
// primo del mese successivo, quindi gestisce da solo anche il 29 febbraio.
const ultimoGiorno = (anno, mese) => new Date(Number(anno), Number(mese), 0).getDate();
const daData = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : "");

/**
 * Filtro periodo del report: anno, mese, oppure intervallo libero.
 *
 * Non filtra niente da solo — chiede al server di rifare il conto sul periodo.
 * Filtrare a video sarebbe sbagliato: il report arriva gia' aggregato (conteggi,
 * medie, distribuzioni), e da una somma non si puo' tornare indietro agli
 * addendi di un mese.
 *
 * Anno e mese partono da soli: una scelta sola e' gia' completa. L'intervallo
 * libero no — si scrive in due campi, e chiedere il report appena si compila il
 * primo significa non lasciare il tempo di compilare il secondo. Le due date si
 * tengono quindi in una bozza locale e partono con **Applica**.
 */
const FiltroPeriodo = ({ anni, da, a, anno, mese, loading, onCambia, onAzzera, senzaData }) => {
  const [bozza, setBozza] = useState({ da, a });
  // Riallinea la bozza quando il periodo cambia da fuori (anno/mese o Azzera).
  useEffect(() => { setBozza({ da, a }); }, [da, a]);

  const attivo = !!(da || a);
  const inSospeso = bozza.da !== da || bozza.a !== a;
  const applica = () => { if (inSospeso) onCambia({ da: bozza.da, a: bozza.a }); };

  return (
    <div className="filtro">
      <span className="fl">Periodo</span>

      <select className="fin" value={anno} disabled={loading}
        onChange={(e) => onCambia({ anno: e.target.value, mese: e.target.value ? mese : "" })}>
        <option value="">Tutti gli anni</option>
        {anni.map((y) => <option key={y} value={y}>{y}</option>)}
      </select>

      <select className="fin" value={mese} disabled={loading || !anno}
        title={anno ? "" : "Scegli prima l'anno"}
        onChange={(e) => onCambia({ anno, mese: e.target.value })}>
        <option value="">Tutto l'anno</option>
        {MESI.map((m, i) => <option key={m} value={due(i + 1)}>{m}</option>)}
      </select>

      <span className="fl fl-sep">oppure dal</span>
      <input type="date" className="fin fin-date" value={bozza.da} disabled={loading}
        onChange={(e) => setBozza(b => ({ ...b, da: e.target.value }))}
        onKeyDown={(e) => { if (e.key === "Enter") applica(); }} />
      <span className="fl">al</span>
      <input type="date" className="fin fin-date" value={bozza.a} disabled={loading}
        onChange={(e) => setBozza(b => ({ ...b, a: e.target.value }))}
        onKeyDown={(e) => { if (e.key === "Enter") applica(); }} />

      {inSospeso && (
        <button type="button" className="pill pill-go" onClick={applica} disabled={loading}>
          <Check size={13} /> Applica
        </button>
      )}

      {attivo && (
        <button type="button" className="pill" onClick={onAzzera} disabled={loading}>
          <X size={13} /> Azzera
        </button>
      )}

      <span className="fnota">
        {inSospeso
          ? "Premi Applica per aggiornare il report"
          : attivo
            ? (da && a ? `dal ${daData(da)} al ${daData(a)}` : da ? `dal ${daData(da)}` : `fino al ${daData(a)}`)
              + (senzaData > 0 ? ` · ${senzaData} operazion${senzaData === 1 ? 'e senza data esclusa' : 'i senza data escluse'}` : '')
            : ""}
      </span>
    </div>
  );
};

const ReportView = ({ data, title = "Report Generale", subtitle = "", onRefresh, loading = false, isNational = false, logoBase64 = null }) => {
  const [periodo, setPeriodo] = useState({ da: "", a: "", anno: "", mese: "" });
  // Gli anni selezionabili si ricavano dai dati, ma l'elenco non deve
  // RESTRINGERSI quando un filtro e' attivo: altrimenti scegliendo il 2025 si
  // perderebbe la voce per tornare al 2026. Quindi cresce e non cala mai.
  const [anni, setAnni] = useState([]);
  useEffect(() => {
    const trovati = (data?.trend_mensile || []).map(r => String(r.mese || "").slice(0, 4)).filter(Boolean);
    if (!trovati.length) return;
    setAnni(prev => {
      const uniti = Array.from(new Set([...prev, ...trovati])).sort().reverse();
      return uniti.length === prev.length && uniti.every((y, i) => y === prev[i]) ? prev : uniti;
    });
  }, [data]);

  const cambiaPeriodo = (patch) => {
    // Anno/mese e intervallo libero sono due modi di dire la stessa cosa: chi
    // usa uno azzera l'altro, cosi' a schermo non restano due periodi diversi.
    let next;
    if (patch.anno !== undefined || patch.mese !== undefined) {
      const anno = patch.anno ?? periodo.anno;
      const mese = patch.mese ?? periodo.mese;
      if (!anno) next = { da: "", a: "", anno: "", mese: "" };
      else if (!mese) next = { da: `${anno}-01-01`, a: `${anno}-12-31`, anno, mese: "" };
      else next = { da: `${anno}-${mese}-01`, a: `${anno}-${mese}-${due(ultimoGiorno(anno, mese))}`, anno, mese };
    } else {
      next = { da: patch.da || "", a: patch.a || "", anno: "", mese: "" };
    }
    setPeriodo(next);
    if (onRefresh) onRefresh({ da: next.da, a: next.a });
  };
  const azzeraPeriodo = () => {
    setPeriodo({ da: "", a: "", anno: "", mese: "" });
    if (onRefresh) onRefresh({ da: "", a: "" });
  };

  if (!data) return null;
  const t = data.totali || {};

  const downloadPdf = async () => {
    try {
      const sub2 = (periodo.da || periodo.a)
        ? `${subtitle}${subtitle ? " · " : ""}Periodo: ${periodo.da ? "dal " + daData(periodo.da) : ""}${periodo.a ? (periodo.da ? " al " : "fino al ") + daData(periodo.a) : ""}`
        : subtitle;
      await generatePDF.report(data, { title, subtitle: sub2, logoBase64 });
      toast.success("PDF generato!");
    } catch (e) {
      toast.error("Errore generazione PDF");
    }
  };
  const dsp = data.dispersi || {};
  const nProfili = dsp.per_profilo_isrid?.length || 0;
  const hasDispersi = (t.schede_disperso ?? 0) > 0;

  return (
    <div className="tas-report">
      <style>{STYLE}</style>

      <div className="topbar">
        <div className="title-group">
          <div className="tg-badge">📊</div>
          <div>
            <h1>{title}</h1>
            {subtitle && <p>{subtitle}</p>}
          </div>
        </div>
        <div className="actions">
          <span className="pill"><span className="dot" />Dati aggiornati</span>
          <button type="button" className="pill" onClick={downloadPdf}>
            <FileDown size={13} /> Scarica PDF
          </button>
          {onRefresh && (
            <button type="button" className="pill" onClick={() => onRefresh({ da: periodo.da, a: periodo.a })} disabled={loading}>
              <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Aggiorna
            </button>
          )}
        </div>
      </div>

      <FiltroPeriodo
        anni={anni} da={periodo.da} a={periodo.a} anno={periodo.anno} mese={periodo.mese}
        loading={loading} onCambia={cambiaPeriodo} onAzzera={azzeraPeriodo}
        senzaData={data.periodo?.senza_data_escluse || 0}
      />

      <div className="kpis">
        <Kpi label="Operazioni totali" value={t.operazioni_totali}
          sub={`${t.operazioni_attive ?? 0} attive · ${t.operazioni_chiuse ?? 0} chiuse`
            + ((t.esercitazioni_escluse ?? 0) > 0
              // Dichiarato, non nascosto: le esercitazioni non entrano nelle
              // statistiche, ma un totale piu' basso senza spiegazione sembra
              // un dato perso.
              ? ` · ${t.esercitazioni_escluse} esercitazion${t.esercitazioni_escluse === 1 ? 'e esclusa' : 'i escluse'}`
              : '')} accent="var(--tr-orange)" />
        <Kpi label="Schede Disperso" value={t.schede_disperso} accent="var(--tr-purple)" />
        <Kpi label="Op. Attive" value={t.operazioni_attive} accent="var(--tr-green)" />
        <Kpi label="Op. Chiuse" value={t.operazioni_chiuse} accent="var(--tr-blue)" />
      </div>

      {(t.operazioni_totali ?? 0) === 0 ? (
        <div className="empty-card">
          <p>Nessun dato disponibile.</p>
          <p className="sub">I report si popoleranno automaticamente non appena vengono create operazioni nel sistema.</p>
        </div>
      ) : (
        <div className="grid">
          {data.trend_mensile?.length > 0 && (
            <Panel icon="📈" title="Trend interventi per mese">
              <Trend data={data.trend_mensile} />
            </Panel>
          )}

          {isNational && data.per_regione?.length > 0 && (
            <Panel icon="🗺️" title="Operazioni per regione" count={`${t.operazioni_totali} totali`}>
              <Bars data={data.per_regione} color="var(--tr-blue)" />
              <TopFoot data={data.per_regione} leadText="Regione con più casi" totalText="del totale" />
            </Panel>
          )}

          {nProfili > 0 && (
            <Panel icon="🧭" title="Dispersi per profilo ISRID" count={`${nProfili} profili`}>
              <Bars data={dsp.per_profilo_isrid} color="var(--tr-purple)" />
              <TopFoot data={dsp.per_profilo_isrid} leadText="Profilo più frequente" totalText="dei dispersi" />
            </Panel>
          )}

          {hasDispersi && (
            <Panel icon="🔍" title="Dispersi · demografica" count={`${t.schede_disperso} totali`}>
              <div className="demo">
                <Donut data={dsp.per_sesso ?? []} />
                <div className="age">
                  <div className="ahead">Per fascia d'età</div>
                  <Bars data={dsp.per_eta ?? []} color="var(--tr-purple)" ageStyle />
                </div>
              </div>
            </Panel>
          )}

          {hasDispersi && (dsp.per_esito?.length ?? 0) > 0 && (
            <Panel icon="✅" title="Esiti operazioni di ricerca">
              <Donut data={dsp.per_esito} />
            </Panel>
          )}

          {hasDispersi && (dsp.per_esito_positivo?.length ?? 0) > 0 && (
            <Panel icon="🚑" title="Salvataggi e ritrovamenti"
              count={`${(dsp.per_esito_positivo || []).reduce((s, d) => s + d.count, 0)} esiti positivi`}>
              <Donut data={dsp.per_esito_positivo} />
              <div className="trend-foot">
                <span className="tf">Salvataggio = persona ritrovata viva (INDIA 0-3)</span>
                <span className="tf">Ritrovamento = persona deceduta (INDIA 4)</span>
              </div>
            </Panel>
          )}

          {hasDispersi && (dsp.per_codice_india?.length ?? 0) > 0 && (
            <Panel icon="🇮🇳" title="Distribuzione codice INDIA">
              <Bars data={dsp.per_codice_india} color="#06b6d4" />
            </Panel>
          )}

          {hasDispersi && (dsp.per_problemi_mentali?.length ?? 0) > 0 && (
            <Panel icon="🧠" title="Dispersi con problemi mentali / cognitivi">
              <Bars data={dsp.per_problemi_mentali} color="#ec4899" />
            </Panel>
          )}
        </div>
      )}
    </div>
  );
};

export default ReportView;
